/*
 * SPDX-License-Identifier: Apache-2.0
 *
 * The OpenSearch Contributors require contributions made to
 * this file be licensed under the Apache-2.0 license or a
 * compatible open source license.
 *
 * Modifications Copyright OpenSearch Contributors. See
 * GitHub history for details.
 */

/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import React, { Component } from 'react';
import PropTypes from "prop-types";
import { isString } from '../../services/predicate';
import { OuiContextMenuItem, OuiContextMenuPanel } from '../context_menu';
import { OuiPopover } from '../popover';
import { OuiButtonIcon } from '../button';
import { OuiToolTip } from '../tool_tip';
import { OuiI18n } from '../i18n';
function actionIsCustomItemAction(action) {
  return action.hasOwnProperty('render');
}
export class CollapsedItemActions extends Component {
  popoverDiv = null;
  state = {
    popoverOpen: false
  };
  togglePopover = () => {
    this.setState(prevState => ({
      popoverOpen: !prevState.popoverOpen
    }));
  };
  closePopover = () => {
    this.setState({
      popoverOpen: false
    });
  };
  onPopoverBlur = () => {
    // you must be asking... WTF? I know... but this timeout is
    // required to make sure we process the onBlur events after the initial
    // event cycle. Reference:
    // https://medium.com/@jessebeach/dealing-with-focus-and-blur-in-a-composite-widget-in-react-90d3c3b49a9b
    window.requestAnimationFrame(() => {
      if (!this.popoverDiv.contains(document.activeElement) && this.props.onBlur) {
        this.props.onBlur();
      }
    });
  };
  registerPopoverDiv = popoverDiv => {
    if (!this.popoverDiv) {
      this.popoverDiv = popoverDiv;
      this.popoverDiv.addEventListener('focusout', this.onPopoverBlur);
    }
  };
  componentWillUnmount() {
    if (this.popoverDiv) {
      this.popoverDiv.removeEventListener('focusout', this.onPopoverBlur);
    }
  }
  onClickItem = onClickAction => {
    this.closePopover();
    if (onClickAction) {
      onClickAction();
    }
  };
  render() {
    const {
      actions,
      itemId,
      item,
      actionEnabled,
      onFocus,
      className
    } = this.props;
    const isOpen = this.state.popoverOpen;
    let allDisabled = true;
    const controls = actions.reduce((controls, action, index) => {
      const key = `action_${itemId}_${index}`;
      const available = action.available ? action.available(item) : true;
      if (!available) {
        return controls;
      }
      const enabled = actionEnabled(action);
      allDisabled = allDisabled && !enabled;
      if (actionIsCustomItemAction(action)) {
        const customAction = action;
        const actionControl = customAction.render(item, enabled);
        const actionControlOnClick = actionControl && actionControl.props && actionControl.props.onClick;
        controls.push(<OuiContextMenuItem key={key} onClick={() => this.onClickItem(actionControlOnClick ? () => actionControlOnClick(item) : undefined)}>
              {actionControl}
            </OuiContextMenuItem>);
      } else {
        const {
          onClick,
          name,
          href,
          target,
          'data-test-subj': dataTestSubj
        } = action;
        const buttonIcon = action.icon;
        let icon;
        if (buttonIcon) {
          icon = isString(buttonIcon) ? buttonIcon : buttonIcon(item);
        }
        const buttonContent = typeof name === 'function' ? name(item) : name;
        controls.push(<OuiContextMenuItem key={key} disabled={!enabled} href={href} target={target} icon={icon} data-test-subj={dataTestSubj} onClick={() => this.onClickItem(onClick ? () => onClick(item) : undefined)}>
              {buttonContent}
            </OuiContextMenuItem>);
      }
      return controls;
    }, []);
    const popoverButton = <OuiI18n token="ouiCollapsedItemActions.allActions" default="All actions">
        {allActions => <OuiButtonIcon className={className} aria-label={allActions} iconType="boxesHorizontal" color="text" isDisabled={allDisabled} onClick={this.togglePopover.bind(this)} onFocus={onFocus} data-test-subj="ouiCollapsedItemActionsButton" />}
      </OuiI18n>;
    const withTooltip = !allDisabled && <OuiI18n token="ouiCollapsedItemActions.allActions" default="All actions">
        {allActions => <OuiToolTip content={allActions} delay="long">
            {popoverButton}
          </OuiToolTip>}
      </OuiI18n>;
    return <OuiPopover className={className} popoverRef={this.registerPopoverDiv} id={`${itemId}-actions`} isOpen={isOpen} button={withTooltip || popoverButton} closePopover={this.closePopover} panelPaddingSize="none" anchorPosition="leftCenter">
        <OuiContextMenuPanel items={controls} />
      </OuiPopover>;
  }
}
CollapsedItemActions.propTypes = {
  actions: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.shape({
    /**
       * The type of action
       */
    type: PropTypes.oneOfType([PropTypes.oneOf(["button"]), PropTypes.oneOf(["icon"]).isRequired]),
    /**
       * Defines the color of the button
       */
    color: PropTypes.oneOfType([PropTypes.oneOfType([PropTypes.oneOf(["primary", "danger", "text", "ghost", "success", "warning"]).isRequired, PropTypes.func.isRequired]), PropTypes.oneOfType([PropTypes.oneOf(["accent", "danger", "ghost", "primary", "subdued", "success", "text", "warning"]).isRequired, PropTypes.func.isRequired])]),
    /**
       * The display name of the action (will be the button caption)
       */
    /**
       * The display name of the action (will be the button caption)
       */
    name: PropTypes.oneOfType([PropTypes.node.isRequired, PropTypes.func.isRequired]).isRequired,
    /**
       * Describes the action (will be the button title)
       */
    /**
       * Describes the action (will be the button title)
       */
    description: PropTypes.string.isRequired,
    /**
       * A handler function to execute the action
       */
    /**
       * A handler function to execute the action
       */
    onClick: PropTypes.func,
    href: PropTypes.string,
    target: PropTypes.string,
    /**
       * A callback function that determines whether the action is available
       */
    /**
       * A callback function that determines whether the action is available
       */
    available: PropTypes.func,
    /**
       * A callback function that determines whether the action is enabled
       */
    /**
       * A callback function that determines whether the action is enabled
       */
    enabled: PropTypes.func,
    isPrimary: PropTypes.bool,
    "data-test-subj": PropTypes.string,
    /**
       * Associates an icon with the button
       */
    icon: PropTypes.oneOfType([PropTypes.oneOf(["accessibility", "addBookmark", "addDataApp", "advancedSettingsApp", "aggregate", "alert", "analyzeEvent", "annotation", "anomalyDetection", "apmApp", "apmTrace", "apps", "appSearchApp", "arrowDown", "arrowLeft", "arrowRight", "arrowUp", "asterisk", "auditbeatApp", "beaker", "bell", "bellSlash", "bolt", "bookOpen", "boxesHorizontal", "boxesVertical", "branch", "broom", "brush", "bug", "bullseye", "calendar", "canvasApp", "codeApp", "chatLeft", "chatRight", "check", "checkInCircleEmpty", "checkInCircleFilled", "cheer", "classificationJob", "clock", "cloudDrizzle", "cloudStormy", "cloudSunny", "color", "compass", "compute", "console", "consoleApp", "continuityAbove", "continuityAboveBelow", "continuityBelow", "continuityWithin", "controlsHorizontal", "controlsVertical", "copy", "copyClipboard", "createAdvancedJob", "createMultiMetricJob", "createPopulationJob", "createSingleMetricJob", "cross", "crossClusterReplicationApp", "crosshairs", "crossInACircleFilled", "crossInCircleEmpty", "crossInCircleFilled", "currency", "cut", "dashboard", "dashboardApp", "database", "dataVisualizer", "devToolsApp", "discoverApp", "document", "documentation", "documentEdit", "documents", "dot", "download", "dockedBottom", "dockedDetached", "dockedLeft", "dockedRight", "dockedTakeover", "dockedTop", "editorAlignCenter", "editorAlignLeft", "editorAlignRight", "editorBold", "editorCodeBlock", "editorComment", "editorDistributeHorizontal", "editorDistributeVertical", "editorHeading", "editorItalic", "editorItemAlignLeft", "editorItemAlignBottom", "editorItemAlignCenter", "editorItemAlignMiddle", "editorItemAlignRight", "editorItemAlignTop", "editorLink", "editorOrderedList", "editorPositionBottomLeft", "editorPositionBottomRight", "editorPositionTopLeft", "editorPositionTopRight", "editorRedo", "editorStrike", "editorTable", "editorUnderline", "editorUndo", "editorUnorderedList", "email", "empty", "emsApp", "eql", "eraser", "exit", "expand", "expandMini", "exportAction", "eye", "eyeClosed", "faceHappy", "faceNeutral", "faceSad", "filebeatApp", "filter", "flag", "fold", "folderCheck", "folderClosed", "folderExclamation", "folderOpen", "frameNext", "framePrevious", "fullScreen", "fullScreenExit", "function", "functionAdd", "gear", "generate", "generateContent", "gisApp", "glasses", "globe", "grab", "grabHorizontal", "graphApp", "grid", "grokApp", "heart", "heartbeatApp", "heatmap", "help", "history", "home", "iInCircle", "image", "importAction", "indexClose", "indexEdit", "indexFlush", "indexManagementApp", "indexMapping", "indexOpen", "indexPatternApp", "indexRollupApp", "indexRuntime", "indexSettings", "inputOutput", "inspect", "integrationGeneral", "integrationObservability", "integrationSearch", "integrationSecurity", "invert", "ip", "keyboardShortcut", "kqlField", "kqlFunction", "kqlOperand", "kqlSelector", "kqlValue", "layers", "lensApp", "lineChart", "link", "list", "listAdd", "lock", "lockOpen", "logsApp", "logoAerospike", "logoApache", "logoAppSearch", "logoAWS", "logoAWSMono", "logoAzure", "logoAzureMono", "logoBeats", "logoBusinessAnalytics", "logoCeph", "logoCloud", "logoCloudEnterprise", "logoCode", "logoCodesandbox", "logoCouchbase", "logoDocker", "logoDropwizard", "logoElastic", "logoElasticsearch", "logoElasticStack", "logoEnterpriseSearch", "logoEtcd", "logoGCP", "logoGCPMono", "logoGithub", "logoGmail", "logoGolang", "logoGoogleG", "logoHAproxy", "logoIBM", "logoIBMMono", "logoKafka", "logoKibana", "logoKubernetes", "logoLogging", "logoLogstash", "logoMaps", "logoMemcached", "logoMetrics", "logoMongodb", "logoMySQL", "logoNginx", "logoObservability", "logoOpenSearch", "logoOsquery", "logoPhp", "logoPostgres", "logoPrometheus", "logoRabbitmq", "logoRedis", "logoSecurity", "logoSiteSearch", "logoSketch", "logoSlack", "logoUptime", "logoWebhook", "logoWindows", "logoWorkplaceSearch", "logstashFilter", "logstashIf", "logstashInput", "logstashOutput", "logstashQueue", "machineLearningApp", "magnet", "magnifyWithMinus", "magnifyWithPlus", "managementApp", "mapMarker", "memory", "menu", "menuDown", "menuLeft", "menuRight", "menuUp", "merge", "metricbeatApp", "metricsApp", "minimize", "minus", "minusInCircle", "minusInCircleFilled", "mobile", "monitoringApp", "moon", "nested", "node", "notebookApp", "number", "offline", "online", "outlierDetectionJob", "package", "packetbeatApp", "pageSelect", "pagesSelect", "partial", "paperClip", "pause", "pencil", "percent", "pin", "pinFilled", "pipelineApp", "play", "playFilled", "plus", "plusInCircle", "plusInCircleFilled", "polygon", "popout", "power", "pulse", "push", "questionInCircle", "quote", "radar", "radius", "recent", "recentlyViewedApp", "redeploy", "refresh", "regressionJob", "reporter", "reportingApp", "returnKey", "rocket", "save", "savedObjectsApp", "scale", "search", "searchProfilerApp", "securityAnalyticsApp", "securityApp", "securitySignal", "securitySignalDetected", "securitySignalResolved", "shard", "share", "snowflake", "sortable", "sortDown", "sortLeft", "sortRight", "sortUp", "spacesApp", "sparkleFilled", "sqlApp", "starEmpty", "starEmptySpace", "starFilled", "starFilledSpace", "starMinusEmpty", "starMinusFilled", "starPlusEmpty", "starPlusFilled", "stats", "stop", "stopFilled", "stopSlash", "storage", "string", "submodule", "swatchInput", "symlink", "tableOfContents", "tableDensityExpanded", "tableDensityCompact", "tableDensityNormal", "tag", "tear", "temperature", "thumbsDown", "thumbsUp", "timeline", "timelionApp", "timeslider", "training", "trash", "upgradeAssistantApp", "uptimeApp", "undeploy", "unfold", "unlink", "user", "users", "usersRolesApp", "vector", "videoPlayer", "visArea", "visAreaStacked", "visBarHorizontal", "visBarHorizontalStacked", "visBarVertical", "visBarVerticalStacked", "visBuilder", "visBuilderSavedObject", "visGauge", "visGoal", "visLine", "visMapCoordinate", "visMapRegion", "visMetric", "visPie", "visQueryDQL", "visQueryPPL", "visQueryPromQL", "visQuerySQL", "visTable", "visTagCloud", "visText", "visTimelion", "visualizeApp", "visVega", "visVisualBuilder", "watchesApp", "wordWrap", "wordWrapDisabled", "workplaceSearchApp", "wrench", "tokenClass", "tokenProperty", "tokenEnum", "tokenVariable", "tokenMethod", "tokenAnnotation", "tokenException", "tokenInterface", "tokenParameter", "tokenField", "tokenElement", "tokenFunction", "tokenBoolean", "tokenString", "tokenArray", "tokenNumber", "tokenConstant", "tokenObject", "tokenEvent", "tokenKey", "tokenNull", "tokenStruct", "tokenPackage", "tokenOperator", "tokenEnumMember", "tokenRepo", "tokenSymbol", "tokenFile", "tokenModule", "tokenNamespace", "tokenDate", "tokenIP", "tokenNested", "tokenAlias", "tokenShape", "tokenGeo", "tokenRange", "tokenBinary", "tokenJoin", "tokenPercolator", "tokenFlattened", "tokenRankFeature", "tokenRankFeatures", "tokenKeyword", "tokenCompletionSuggester", "tokenDenseVector", "tokenText", "tokenTokenCount", "tokenSearchType", "tokenHistogram", "wsAnalytics", "wsEssentials", "wsObservability", "wsSearch", "wsSecurityAnalytics", "wsSelector", "navAdministration", "navAiFlow", "navAlerting", "navAnomalyDetection", "navDashboards", "navData", "navDetectionRules", "navDevtools", "navDiscover", "navExperiments", "navGetStarted", "navInfo", "navInfra", "navIntegrations", "navJudgements", "navManage", "navMaps", "navModels", "navNotebooks", "navNotifications", "navOverview", "navQuerySets", "navReports", "navSecurityCases", "navSecurityFindings", "navServiceMap", "navServices", "navSlos", "navThreatIntel", "navTicketing", "navUi", "navSearchConfigurationsln"]).isRequired, PropTypes.func.isRequired])
  }).isRequired, PropTypes.shape({
    /**
       * The function that renders the action. Note that the returned node is expected to have `onFocus` and `onBlur` functions
       */
    render: PropTypes.func.isRequired,
    /**
       * A callback that defines whether the action is available
       */
    available: PropTypes.func,
    /**
       * A callback that defines whether the action is enabled
       */
    enabled: PropTypes.func,
    isPrimary: PropTypes.bool
  }).isRequired]).isRequired).isRequired,
  item: PropTypes.any.isRequired,
  itemId: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.number.isRequired]).isRequired,
  actionEnabled: PropTypes.func.isRequired,
  className: PropTypes.string,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func
};
try {
  CollapsedItemActions.__docgenInfo = {
    tags: {},
    filePath: '/codebuild/output/src2210237963/src/src/components/basic_table/collapsed_item_actions.tsx',
    description: '',
    displayName: 'CollapsedItemActions',
    methods: [],
    props: {
      actions: {
        defaultValue: null,
        description: '',
        name: 'actions',
        parent: {
          fileName: 'src/src/components/basic_table/collapsed_item_actions.tsx',
          name: 'CollapsedItemActionsProps'
        },
        declarations: [{
          fileName: 'src/src/components/basic_table/collapsed_item_actions.tsx',
          name: 'CollapsedItemActionsProps'
        }],
        required: true,
        type: {
          name: 'Action<T>[]'
        }
      },
      item: {
        defaultValue: null,
        description: '',
        name: 'item',
        parent: {
          fileName: 'src/src/components/basic_table/collapsed_item_actions.tsx',
          name: 'CollapsedItemActionsProps'
        },
        declarations: [{
          fileName: 'src/src/components/basic_table/collapsed_item_actions.tsx',
          name: 'CollapsedItemActionsProps'
        }],
        required: true,
        type: {
          name: 'T'
        }
      },
      itemId: {
        defaultValue: null,
        description: '',
        name: 'itemId',
        parent: {
          fileName: 'src/src/components/basic_table/collapsed_item_actions.tsx',
          name: 'CollapsedItemActionsProps'
        },
        declarations: [{
          fileName: 'src/src/components/basic_table/collapsed_item_actions.tsx',
          name: 'CollapsedItemActionsProps'
        }],
        required: true,
        type: {
          name: 'ItemIdResolved'
        }
      },
      actionEnabled: {
        defaultValue: null,
        description: '',
        name: 'actionEnabled',
        parent: {
          fileName: 'src/src/components/basic_table/collapsed_item_actions.tsx',
          name: 'CollapsedItemActionsProps'
        },
        declarations: [{
          fileName: 'src/src/components/basic_table/collapsed_item_actions.tsx',
          name: 'CollapsedItemActionsProps'
        }],
        required: true,
        type: {
          name: '(action: Action<T>) => boolean'
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'src/src/components/basic_table/collapsed_item_actions.tsx',
          name: 'CollapsedItemActionsProps'
        },
        declarations: [{
          fileName: 'src/src/components/basic_table/collapsed_item_actions.tsx',
          name: 'CollapsedItemActionsProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      onFocus: {
        defaultValue: null,
        description: '',
        name: 'onFocus',
        parent: {
          fileName: 'src/src/components/basic_table/collapsed_item_actions.tsx',
          name: 'CollapsedItemActionsProps'
        },
        declarations: [{
          fileName: 'src/src/components/basic_table/collapsed_item_actions.tsx',
          name: 'CollapsedItemActionsProps'
        }],
        required: false,
        type: {
          name: '(event: FocusEvent<Element, Element>) => void'
        }
      },
      onBlur: {
        defaultValue: null,
        description: '',
        name: 'onBlur',
        parent: {
          fileName: 'src/src/components/basic_table/collapsed_item_actions.tsx',
          name: 'CollapsedItemActionsProps'
        },
        declarations: [{
          fileName: 'src/src/components/basic_table/collapsed_item_actions.tsx',
          name: 'CollapsedItemActionsProps'
        }],
        required: false,
        type: {
          name: '() => void'
        }
      }
    },
    extendedInterfaces: ['CollapsedItemActionsProps']
  };
} catch (e) {}